import { useMutation } from '@apollo/client';
import { zodResolver } from '@hookform/resolvers/zod';
import React from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { CurrencyInput, currencyStringToCentsSchema } from '@eluve/blocks';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  Input,
  Stack,
  useToast,
} from '@eluve/components';
import {
  MedicalCodeTypesEnum,
  ProceduralMedicalCodeTypes,
} from '@eluve/graphql-types';
import { useTranslation } from '@eluve/i18n';
import { UseDialog } from '@eluve/utility-hooks';
import { billingCodeModifiersSchema } from '@eluve/utils';

import { createUserGroupCodeConfigMutation } from './user-group-configs.operations';

type SelectedCode = {
  id: string;
  code: string;
  codeType: MedicalCodeTypesEnum;
  description: string | null;
} | null;

export const AddUserGroupConfigModal: React.FC<{
  userGroupId: string;
  dialog: UseDialog;
  code: SelectedCode;
  onSuccess?: () => void;
}> = ({
  dialog: { isDialogOpen, toggleDialog },
  userGroupId,
  code,
  onSuccess,
}) => {
  const isProceduralCode = ProceduralMedicalCodeTypes.includes(
    code?.codeType ?? '',
  );
  const toast = useToast();
  const { t } = useTranslation();

  const userGroupConfigFormSchema = z.object({
    label: z.string().optional(),
    price: isProceduralCode ? currencyStringToCentsSchema : z.nan(),
    modifiers: billingCodeModifiersSchema.optional(),
  });

  const defaultFormValues = {
    label: '',
    price: NaN,
    modifiers: '',
  };

  type UserGroupConfigForm = z.infer<typeof userGroupConfigFormSchema>;

  const form = useForm<UserGroupConfigForm>({
    defaultValues: defaultFormValues,
    resolver: zodResolver(userGroupConfigFormSchema),
  });

  const [createUserGroupCodeConfig] = useMutation(
    createUserGroupCodeConfigMutation,
    {
      onCompleted: () => {
        onSuccess?.();
        toast.success(t('tenantAdmin.codes.configCreated'));
        toggleDialog();
        form.reset(defaultFormValues);
      },
      onError: () => toast.error(t('tenantAdmin.codes.configCreateFailed')),
    },
  );

  const handleOpenChange = () => {
    toggleDialog();
    form.reset(defaultFormValues);
  };

  const onSubmit = async ({ price, label, modifiers }: UserGroupConfigForm) => {
    if (!code) {
      return;
    }

    createUserGroupCodeConfig({
      variables: {
        userGroupId,
        label: label ? label : code.description,
        price: price ?? null,
        modifiers:
          modifiers && modifiers.length > 0 ? modifiers.split(',') : null,
        codeId: code.id,
      },
    });
  };

  return (
    <Dialog open={isDialogOpen} onOpenChange={handleOpenChange}>
      <Form {...form}>
        <DialogContent size="lg">
          <DialogHeader>
            <DialogTitle>{t('tenantAdmin.codes.newConfigTitle')}</DialogTitle>
          </DialogHeader>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <Stack className="my-4" gap={4}>
              <FormField
                control={form.control}
                name="label"
                render={({ field }) => (
                  <FormItem className="w-full">
                    <FormLabel>{t('tenantAdmin.codes.customLabel')}</FormLabel>
                    <FormControl>
                      <Input
                        placeholder={
                          code?.description ??
                          t('tenantAdmin.codes.customLabel')
                        }
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>
                      {t('tenantAdmin.codes.customLabelDescription')}
                    </FormDescription>
                  </FormItem>
                )}
              />

              {isProceduralCode && (
                <FormField
                  control={form.control}
                  name="price"
                  render={({ field }) => (
                    <FormItem className="w-full">
                      <FormLabel>
                        {t('tenantAdmin.codes.priceRequiredLabel')}
                      </FormLabel>
                      <FormControl>
                        <CurrencyInput
                          {...field}
                          value={
                            field?.value && !isNaN(field.value)
                              ? (field.value?.toString() ?? '')
                              : ''
                          }
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              )}

              {isProceduralCode && (
                <FormField
                  control={form.control}
                  name="modifiers"
                  render={({ field }) => (
                    <FormItem className="w-full">
                      <FormLabel>
                        {t('tenantAdmin.codes.modifiersLabel')}
                      </FormLabel>
                      <FormControl>
                        <Input {...field} value={field.value ?? ''} />
                      </FormControl>
                      <FormDescription>
                        {t('tenantAdmin.codes.modifiersDescription')}
                      </FormDescription>
                    </FormItem>
                  )}
                />
              )}
            </Stack>
            <DialogFooter className="mt-2">
              <Button type="submit">{t('tenantAdmin.common.create')}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Form>
    </Dialog>
  );
};
